import { MdPlayArrow, MdTableView } from 'react-icons/md';
import { Appearance } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { CustomModalEditWidgetHeader } from '@Components/ModalEditWidget/modal-edit-constants';
import { Spinner } from '@Components/Spinner';
import { TruncatedTextTooltip } from '@Components/TruncatedTextTooltip';

export const PAGE_TITLE = 'Query Exporter';
export const PAGE_TYPE = 'Query Exporter';
export const ROW_HEIGHT = 80;
const QUERY_TEXTAREA_ROWS = 14;

export interface QueryRow {
  id: number;
  company: string;
  name: string;
  query: string;
  cron: string;
  tz: string;
  sheet_id: string;
  tab_name: string;
  last_executed: string;
  last_completed: string;
  completed_status: string;
  last_modified: string;
  last_user: string;
}

/** Stable row key for ModalEditWidget's getRowId -- module scope so it's a stable function identity. */
export const getQueryRowId = (row: QueryRow): string => String(row.id);

export interface QueryRowParams {
  insert: QueryRow[];
  update: QueryRow[];
  delete: QueryRow[];
}

export const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Mexico_City',
  'America/Los_Angeles',
  'Europe/London',
  'ETC/UTC'
] as const;

const MAIN_MODAL_WIDTH = 200;

// Kept tight so the fixed-width columns leave the flexed Query column (see QUERY below) enough
// room to actually grow, and the table's total minimum width stays inside the page without
// forcing horizontal scroll on a typical viewport. QUERY here is Query's *minimum* flex width,
// not a fixed width -- it grows to fill whatever space is actually available.
const WIDTHS = {
  ID: 60,
  COMPANY: 120,
  NAME: 140,
  QUERY: 200,
  SHEET_ID: 60,
  TAB_NAME: 100,
  CRON: 100,
  // Fixed, not flex: the timezone strings come from a small known set (TIMEZONE_OPTIONS below),
  // so a width sized to the longest one ("America/Los_Angeles") is predictable and doesn't need
  // to grow.
  TZ: 160,
  COMPLETED_STATUS: 100,
  RUN: 60
} as const;

const MODAL_ROWS = {
  COMPANY: 0,
  NAME: 0,
  QUERY: 3,
  SHEET_ID: 2,
  TAB_NAME: 2,
  CRON: 1,
  TZ: 1,
  // Shares the cron/timezone row -- Run has no visible modal field of its own (it's uneditable),
  // so this just avoids leaving behind an empty modal row.
  RUN: 1
} as const;

// Run only shows once a query has reached a terminal state (FAILED/SUCCESS); otherwise it's
// assumed to already be in flight and shows a spinner instead.
const isRunnable = (completedStatus: string | undefined): boolean =>
  Boolean(completedStatus?.includes('FAILED') || completedStatus?.includes('SUCCESS'));

export const buildQueryExporterHeaders = (
  onRunQuery: (id: number) => void,
  isCronInvalid: (cron: string) => boolean
): CustomModalEditWidgetHeader[] => [
  {
    label: 'ID',
    field: 'id',
    type: 'text',
    width: WIDTHS.ID,
    nonInteractive: true,
    uneditable: true,
    renderer: row => <span data-testid={`row-id-${row.id}`}>{row.id}</span>
  },
  {
    label: 'Company',
    field: 'company',
    type: 'text',
    width: WIDTHS.COMPANY,
    modalRow: MODAL_ROWS.COMPANY,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Name',
    field: 'name',
    type: 'text',
    width: WIDTHS.NAME,
    modalRow: MODAL_ROWS.NAME,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Query',
    field: 'query',
    type: 'query',
    width: WIDTHS.QUERY,
    minFlexWidth: WIDTHS.QUERY,
    flex: 1,
    modalRow: MODAL_ROWS.QUERY,
    modalWidth: MAIN_MODAL_WIDTH,
    modalFlex: 1,
    rows: QUERY_TEXTAREA_ROWS,
    renderer: row => <div className='query-table'>{row.query}</div>
  },
  {
    label: 'Sheet ID',
    field: 'sheet_id',
    type: 'text',
    width: WIDTHS.SHEET_ID,
    modalRow: MODAL_ROWS.SHEET_ID,
    modalWidth: MAIN_MODAL_WIDTH,
    renderer: row => (
      <IconButton
        data-testid={`sheet-link-${row.id}`}
        appearance={Appearance.OUTLINED}
        onClick={e => {
          // The icon sits inside a cell that otherwise opens the row's edit modal on click --
          // stop it from also doing that instead of marking the whole field uneditable, which
          // would drop the Sheet ID input from the edit modal entirely (needed to set the sheet
          // up in the first place, e.g. when adding a new row).
          e.stopPropagation();
          window.open(
            `https://docs.google.com/spreadsheets/d/${row.sheet_id}`,
            '_blank',
            'noreferrer'
          );
        }}
      >
        <MdTableView />
      </IconButton>
    )
  },
  {
    label: 'Tab Name',
    field: 'tab_name',
    type: 'text',
    width: WIDTHS.TAB_NAME,
    modalRow: MODAL_ROWS.TAB_NAME,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Cron',
    field: 'cron',
    type: 'cron',
    width: WIDTHS.CRON,
    modalRow: MODAL_ROWS.CRON,
    modalWidth: MAIN_MODAL_WIDTH,
    renderer: row => <div className='query-cron'>{row.cron}</div>,
    isInvalid: isCronInvalid
  },
  {
    label: 'Timezone',
    field: 'tz',
    type: 'select',
    options: 'tz',
    creatable: false,
    width: WIDTHS.TZ,
    modalRow: MODAL_ROWS.TZ,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Status',
    field: 'completed_status',
    type: 'text',
    width: WIDTHS.COMPLETED_STATUS,
    modalRow: MODAL_ROWS.RUN,
    nonInteractive: true,
    uneditable: true,
    renderer: row => (
      <TruncatedTextTooltip text={row.completed_status ?? ''} className='status-cell' />
    )
  },
  {
    label: 'Run',
    field: 'id',
    type: 'text',
    modalRow: MODAL_ROWS.RUN,
    width: WIDTHS.RUN,
    nonInteractive: true,
    uneditable: true,
    renderer: row =>
      isRunnable(row.completed_status) ? (
        <IconButton
          data-testid={`run-button-${row.id}`}
          appearance={Appearance.OUTLINED}
          onClick={() => onRunQuery(row.id)}
        >
          <MdPlayArrow />
        </IconButton>
      ) : (
        <div data-testid={`run-spinner-${row.id}`}>
          <Spinner />
        </div>
      )
  }
];
